import { useEffect, useMemo, useState } from 'react';
import {
  getSummary,
  getLedger,
  exportLedgerCsv,
  getLedgerItem,
  listPayouts,
  getPayoutDetail,
  getPayoutAccount,
  savePayoutAccount,
  requestPayout,
} from '../api/hostEarningsApi';
import { hostListProperties } from '../api/host';
import { HostSection } from '../components/host/HostLayout';
import { useToast } from '../components/ToastContext';
import { useCurrency } from '../components/CurrencyContext';
import { formatDateRange } from '../utils/dateFormat';

const tabs = [
  { key: 'ledger', label: 'Ledger' },
  { key: 'payouts', label: 'Payouts' },
  { key: 'account', label: 'Payout Account' },
];

const statusTone = (status) => {
  const value = (status || '').toLowerCase();
  if (['available', 'paid', 'processing', 'confirmed', 'completed'].includes(value)) return 'bg-emerald-50 text-emerald-700 ring-emerald-100';
  if (['pending', 'draft'].includes(value)) return 'bg-amber-50 text-amber-700 ring-amber-100';
  if (['on_hold', 'cancelled', 'failed', 'reversed'].includes(value)) return 'bg-rose-50 text-rose-700 ring-rose-100';
  return 'bg-slate-100 text-slate-700 ring-slate-200';
};

const Badge = ({ children, tone }) => (
  <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset ${tone}`}>{children}</span>
);

const StatCard = ({ label, value, hint }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
    <p className="text-sm text-slate-600">{label}</p>
    <p className="text-3xl font-semibold text-slate-900">{value}</p>
    {hint && <p className="text-xs text-slate-500">{hint}</p>}
  </div>
);

const PayoutStatus = ({ status }) => <Badge tone={statusTone(status)}>{status || 'unknown'}</Badge>;

const LoadingRow = ({ colSpan }) => (
  <tr>
    <td colSpan={colSpan} className="px-4 py-3 text-sm text-slate-500">
      Loading...
    </td>
  </tr>
);

const EmptyRow = ({ colSpan, text }) => (
  <tr>
    <td colSpan={colSpan} className="px-4 py-3 text-sm text-slate-500">
      {text}
    </td>
  </tr>
);

const HostEarnings = ({ accessToken }) => {
  const { formatCurrency } = useCurrency();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('ledger');
  const [summary, setSummary] = useState(null);
  const [properties, setProperties] = useState([]);
  const [ledger, setLedger] = useState({ items: [], total: 0, page: 1, limit: 10 });
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerFilters, setLedgerFilters] = useState({ property_id: '', status: '', date_from: '', date_to: '', q: '' });
  const [ledgerDrawer, setLedgerDrawer] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [payoutDetail, setPayoutDetail] = useState(null);
  const [account, setAccount] = useState(null);
  const [savingAccount, setSavingAccount] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [requestingPayout, setRequestingPayout] = useState(false);

  const loadSummary = async () => {
    setLoadingSummary(true);
    try {
      const data = await getSummary(accessToken);
      setSummary(data);
    } catch (err) {
      toast.error(err.message || 'Failed to load summary');
    } finally {
      setLoadingSummary(false);
    }
  };

  const loadProperties = async () => {
    try {
      const res = await hostListProperties(accessToken, { limit: 200 });
      setProperties(res?.items || res || []);
    } catch (_) {
      // ignore
    }
  };

  const loadLedger = async (page = ledger.page || 1) => {
    setLedgerLoading(true);
    try {
      const data = await getLedger(accessToken, { ...ledgerFilters, page, limit: ledger.limit || 10 });
      setLedger({ ...data, page: data.page || page, limit: data.limit || 10 });
    } catch (err) {
      toast.error(err.message || 'Failed to load ledger');
    } finally {
      setLedgerLoading(false);
    }
  };

  const loadPayouts = async () => {
    try {
      const data = await listPayouts(accessToken);
      setPayouts(data || []);
    } catch (err) {
      toast.error(err.message || 'Failed to load payouts');
    }
  };

  const loadAccount = async () => {
    try {
      const data = await getPayoutAccount(accessToken);
      setAccount(data || {});
    } catch (err) {
      toast.error(err.message || 'Failed to load payout account');
    }
  };

  useEffect(() => {
    loadSummary();
    loadLedger(1);
    loadPayouts();
    loadAccount();
    loadProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  useEffect(() => {
    loadLedger(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ledgerFilters.property_id, ledgerFilters.status]);

  const handleExport = async () => {
    try {
      const csv = await exportLedgerCsv(accessToken, ledgerFilters);
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'earnings.csv';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export ready');
    } catch (err) {
      toast.error(err.message || 'Export failed');
    }
  };

  const openLedgerDetail = async (row) => {
    try {
      const detail = await getLedgerItem(accessToken, row.id);
      setLedgerDrawer(detail);
    } catch (err) {
      toast.error(err.message || 'Failed to load ledger detail');
    }
  };

  const openPayoutDetail = async (id) => {
    try {
      const detail = await getPayoutDetail(accessToken, id);
      setPayoutDetail(detail);
    } catch (err) {
      toast.error(err.message || 'Failed to load payout detail');
    }
  };

  const handleSaveAccount = async (e) => {
    e.preventDefault();
    setSavingAccount(true);
    const form = new FormData(e.target);
    const payload = Object.fromEntries(form.entries());
    payload.payout_method = payload.payout_method || 'bank';
    try {
      await savePayoutAccount(accessToken, payload);
      toast.success('Payout account saved');
      await loadAccount();
    } catch (err) {
      toast.error(err.message || 'Failed to save account');
    } finally {
      setSavingAccount(false);
    }
  };

  const handleRequestPayout = async () => {
    setRequestingPayout(true);
    try {
      await requestPayout(accessToken);
      toast.success('Payout requested');
      await Promise.all([loadLedger(), loadPayouts(), loadSummary()]);
    } catch (err) {
      toast.error(err.message || 'Unable to request payout');
    } finally {
      setRequestingPayout(false);
    }
  };

  const summaryCards = useMemo(() => {
    if (!summary) return [];
    return [
      { label: 'Available Balance', value: formatCurrency(summary.available_net || 0, 'INR') },
      { label: 'Pending Earnings', value: formatCurrency(summary.pending_net || 0, 'INR') },
      { label: 'Paid This Month', value: formatCurrency(summary.paid_net || 0, 'INR') },
      {
        label: 'Next Payout',
        value: formatCurrency(summary.next_payout_estimate || 0, 'INR'),
        hint: 'Estimated from available earnings',
      },
    ];
  }, [summary, formatCurrency]);

  const statusOptions = [
    { value: '', label: 'All statuses' },
    { value: 'available', label: 'Available' },
    { value: 'pending', label: 'Pending' },
    { value: 'on_hold', label: 'On hold' },
    { value: 'reversed', label: 'Reversed' },
  ];

  return (
    <div className="space-y-6">
      <HostSection title="Earnings & Payouts" description="Overview of your ledger and upcoming payouts.">
        <div className="p-6 space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {loadingSummary && <p className="text-sm text-slate-500">Loading summary...</p>}
              {!loadingSummary &&
                summaryCards.map((card) => <StatCard key={card.label} label={card.label} value={card.value} hint={card.hint} />)}
            </div>
            <button
              type="button"
              onClick={handleRequestPayout}
              disabled={requestingPayout}
              className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-60"
            >
              {requestingPayout ? 'Requesting...' : 'Request payout'}
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`rounded-full px-4 py-2 text-sm font-semibold ${
                  activeTab === tab.key ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'ledger' && (
            <div className="space-y-4">
              <div className="grid gap-3 md:grid-cols-5">
                <select
                  value={ledgerFilters.property_id}
                  onChange={(e) => setLedgerFilters((f) => ({ ...f, property_id: e.target.value }))}
                  className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
                >
                  <option value="">All properties</option>
                  {properties.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.title}
                    </option>
                  ))}
                </select>
                <select
                  value={ledgerFilters.status}
                  onChange={(e) => setLedgerFilters((f) => ({ ...f, status: e.target.value }))}
                  className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
                >
                  {statusOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <input
                  type="date"
                  value={ledgerFilters.date_from}
                  onChange={(e) => setLedgerFilters((f) => ({ ...f, date_from: e.target.value }))}
                  className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
                />
                <input
                  type="date"
                  value={ledgerFilters.date_to}
                  onChange={(e) => setLedgerFilters((f) => ({ ...f, date_to: e.target.value }))}
                  className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
                />
                <div className="flex items-center gap-2">
                  <input
                    type="search"
                    value={ledgerFilters.q}
                    placeholder="Search booking/property"
                    onChange={(e) => setLedgerFilters((f) => ({ ...f, q: e.target.value }))}
                    className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => loadLedger(1)}
                    className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                  >
                    Apply
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-600">Showing {ledger.items?.length || 0} of {ledger.total || 0}</p>
                <button
                  type="button"
                  onClick={handleExport}
                  className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Export CSV
                </button>
              </div>
              <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-3">Booking</th>
                      <th className="px-4 py-3">Property</th>
                      <th className="px-4 py-3">Dates</th>
                      <th className="px-4 py-3">Gross</th>
                      <th className="px-4 py-3">Commission</th>
                      <th className="px-4 py-3">Refunds</th>
                      <th className="px-4 py-3">Net</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Payout</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {ledgerLoading && <LoadingRow colSpan={9} />}
                    {!ledgerLoading && !ledger.items?.length && <EmptyRow colSpan={9} text="No ledger entries yet." />}
                    {!ledgerLoading &&
                      ledger.items?.map((row) => (
                        <tr
                          key={row.id}
                          className="hover:bg-emerald-50/50 cursor-pointer"
                          onClick={() => openLedgerDetail(row)}
                        >
                          <td className="px-4 py-3 font-semibold text-slate-900">{row.booking_code || `#${row.booking_id}`}</td>
                          <td className="px-4 py-3 text-slate-700">{row.property_title || 'N/A'}</td>
                          <td className="px-4 py-3 text-xs text-slate-600">
                            {formatDateRange(row.check_in_date, row.check_out_date)}
                          </td>
                          <td className="px-4 py-3 font-semibold text-slate-900">{formatCurrency(row.gross_amount, row.currency)}</td>
                          <td className="px-4 py-3 text-slate-700">{formatCurrency(row.platform_commission, row.currency)}</td>
                          <td className="px-4 py-3 text-slate-700">{formatCurrency(row.refunds, row.currency)}</td>
                          <td className="px-4 py-3 font-semibold text-slate-900">{formatCurrency(row.net_earnings, row.currency)}</td>
                          <td className="px-4 py-3">
                            <PayoutStatus status={row.earning_status} />
                          </td>
                          <td className="px-4 py-3">
                            {row.payout_id ? <PayoutStatus status={row.payout_status || 'assigned'} /> : <span className="text-xs text-slate-500">Unassigned</span>}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between text-sm text-slate-600">
                <div>
                  Page {ledger.page || 1} of {Math.max(1, Math.ceil((ledger.total || 1) / (ledger.limit || 10)))}
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => loadLedger(Math.max(1, (ledger.page || 1) - 1))}
                    className="rounded-lg border border-slate-200 px-3 py-1 hover:bg-slate-50"
                    disabled={(ledger.page || 1) <= 1}
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    onClick={() => loadLedger((ledger.page || 1) + 1)}
                    className="rounded-lg border border-slate-200 px-3 py-1 hover:bg-slate-50"
                    disabled={(ledger.page || 1) * (ledger.limit || 10) >= (ledger.total || 0)}
                  >
                    Next
                  </button>
                </div>
              </div>

              {ledgerDrawer && (
                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-md">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-emerald-700">Ledger detail</p>
                      <h3 className="text-lg font-semibold text-slate-900">
                        {ledgerDrawer.booking_code || `Booking #${ledgerDrawer.booking_id}`}
                      </h3>
                      <p className="text-sm text-slate-600">{ledgerDrawer.property_title || 'N/A'}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setLedgerDrawer(null)}
                      className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-200"
                    >
                      Close
                    </button>
                  </div>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <StatCard label="Gross" value={formatCurrency(ledgerDrawer.gross_amount, ledgerDrawer.currency)} />
                    <StatCard label="Commission" value={formatCurrency(ledgerDrawer.platform_commission, ledgerDrawer.currency)} />
                    <StatCard label="Refunds" value={formatCurrency(ledgerDrawer.refunds, ledgerDrawer.currency)} />
                    <StatCard label="Net earnings" value={formatCurrency(ledgerDrawer.net_earnings, ledgerDrawer.currency)} />
                  </div>
                  <div className="mt-4 grid gap-4 lg:grid-cols-2">
                    <div className="rounded-lg border border-slate-200 p-3">
                      <p className="text-sm font-semibold text-slate-900">Breakdown</p>
                      <ul className="mt-2 space-y-1 text-sm text-slate-700">
                        <li>Mandatory fees: {formatCurrency(ledgerDrawer.fees, ledgerDrawer.currency)}</li>
                        <li>GST: {formatCurrency(ledgerDrawer.taxes, ledgerDrawer.currency)}</li>
                        <li>Adjustments: {formatCurrency(ledgerDrawer.adjustments, ledgerDrawer.currency)}</li>
                        <li>Status: {ledgerDrawer.earning_status}</li>
                        <li>Payout: {ledgerDrawer.payout_id ? ledgerDrawer.payout_status || 'assigned' : 'Not assigned'}</li>
                      </ul>
                    </div>
                    <div className="rounded-lg border border-slate-200 p-3">
                      <p className="text-sm font-semibold text-slate-900">Timeline</p>
                      <div className="mt-2 space-y-2 text-sm text-slate-700">
                        {(ledgerDrawer.booking_events || []).map((evt) => (
                          <div key={`${evt.id}-${evt.event_type}`} className="rounded border border-slate-100 bg-slate-50 px-3 py-2">
                            <p className="font-semibold text-slate-900">{evt.event_type}</p>
                            <p className="text-xs text-slate-500">{new Date(evt.created_at).toLocaleString()}</p>
                          </div>
                        ))}
                        {!ledgerDrawer.booking_events?.length && <p className="text-xs text-slate-500">No booking events yet.</p>}
                        {ledgerDrawer.payout_events?.length > 0 && (
                          <div className="pt-2">
                            <p className="text-xs uppercase tracking-wide text-slate-500">Payout events</p>
                            {ledgerDrawer.payout_events.map((evt) => (
                              <div key={`${evt.id}-payout`} className="mt-1 rounded border border-slate-100 bg-emerald-50 px-3 py-2">
                                <p className="font-semibold text-slate-900">{evt.event_type}</p>
                                <p className="text-xs text-slate-600">{new Date(evt.created_at).toLocaleString()}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'payouts' && (
            <div className="space-y-4">
              <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-3">Payout</th>
                      <th className="px-4 py-3">Period</th>
                      <th className="px-4 py-3">Amount</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Processed</th>
                      <th className="px-4 py-3">Reference</th>
                      <th className="px-4 py-3">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {!payouts.length && <EmptyRow colSpan={7} text="No payouts yet." />}
                    {payouts.map((p) => (
                      <tr key={p.id} className="hover:bg-emerald-50/50">
                        <td className="px-4 py-3 font-semibold text-slate-900">#{p.id}</td>
                        <td className="px-4 py-3 text-slate-700">
                          {p.period_start || 'N/A'} - {p.period_end || 'N/A'}
                        </td>
                        <td className="px-4 py-3 font-semibold text-slate-900">{formatCurrency(p.amount, p.currency)}</td>
                        <td className="px-4 py-3">
                          <PayoutStatus status={p.status} />
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-600">{p.processed_at ? new Date(p.processed_at).toLocaleString() : '—'}</td>
                        <td className="px-4 py-3 text-xs text-slate-600">{p.reference || '—'}</td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => openPayoutDetail(p.id)}
                            className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {payoutDetail && (
                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-md">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-emerald-700">Payout</p>
                      <h3 className="text-lg font-semibold text-slate-900">#{payoutDetail.payout?.id}</h3>
                      <p className="text-sm text-slate-600">{formatCurrency(payoutDetail.payout?.amount, payoutDetail.payout?.currency)}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setPayoutDetail(null)}
                      className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-200"
                    >
                      Close
                    </button>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-3">
                    <PayoutStatus status={payoutDetail.payout?.status} />
                    {payoutDetail.payout?.reference && (
                      <span className="rounded-full bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-700">
                        Ref: {payoutDetail.payout.reference}
                      </span>
                    )}
                  </div>
                  <div className="mt-4">
                    <p className="text-sm font-semibold text-slate-900">Assigned ledger rows</p>
                    <div className="mt-2 overflow-x-auto rounded-lg border border-slate-200">
                      <table className="min-w-full divide-y divide-slate-200 text-sm">
                        <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                          <tr>
                            <th className="px-3 py-2">Booking</th>
                            <th className="px-3 py-2">Property</th>
                            <th className="px-3 py-2">Net</th>
                            <th className="px-3 py-2">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {!payoutDetail.ledger_rows?.length && <EmptyRow colSpan={4} text="No ledger rows assigned" />}
                          {payoutDetail.ledger_rows?.map((r) => (
                            <tr key={r.id}>
                              <td className="px-3 py-2 font-semibold text-slate-900">{r.booking_code || `#${r.booking_id}`}</td>
                              <td className="px-3 py-2 text-slate-700">{r.property_title}</td>
                              <td className="px-3 py-2 font-semibold text-slate-900">{formatCurrency(r.net_earnings, r.currency)}</td>
                              <td className="px-3 py-2">
                                <PayoutStatus status={r.earning_status} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="mt-4">
                    <p className="text-sm font-semibold text-slate-900">Events</p>
                    <div className="mt-2 space-y-2">
                      {payoutDetail.events?.map((evt) => (
                        <div key={evt.id} className="rounded border border-slate-100 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                          <p className="font-semibold text-slate-900">{evt.event_type}</p>
                          <p className="text-xs text-slate-500">{new Date(evt.created_at).toLocaleString()}</p>
                        </div>
                      ))}
                      {!payoutDetail.events?.length && <p className="text-xs text-slate-500">No events recorded.</p>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'account' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                <div>
                  <p className="text-sm font-semibold text-slate-900">Current status</p>
                  <p className="text-xs text-slate-600">
                    {account?.verification_status || 'Not submitted'}
                    {account?.rejection_reason ? ` – ${account.rejection_reason}` : ''}
                  </p>
                </div>
                <PayoutStatus status={account?.verification_status || 'pending'} />
              </div>
              <form className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm" onSubmit={handleSaveAccount}>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="text-sm font-semibold text-slate-800">
                    Payout method
                    <select
                      name="payout_method"
                      defaultValue={account?.payout_method || 'bank'}
                      className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                    >
                      <option value="bank">Bank transfer</option>
                      <option value="mobile_money">Mobile money</option>
                      <option value="manual">Manual</option>
                    </select>
                  </label>
                  <label className="text-sm font-semibold text-slate-800">
                    Currency
                    <input
                      name="currency"
                      defaultValue={account?.currency || 'INR'}
                      className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                    />
                  </label>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="text-sm font-semibold text-slate-800">
                    Account name
                    <input
                      name="account_name"
                      defaultValue={account?.account_name || ''}
                      className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                      required
                    />
                  </label>
                  <label className="text-sm font-semibold text-slate-800">
                    Account number / Mobile
                    <input
                      name="account_number"
                      defaultValue={account?.account_number || account?.mobile_money_number || ''}
                      className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                    />
                  </label>
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  <label className="text-sm font-semibold text-slate-800">
                    Bank name
                    <input
                      name="bank_name"
                      defaultValue={account?.bank_name || ''}
                      className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                    />
                  </label>
                  <label className="text-sm font-semibold text-slate-800">
                    IFSC / SWIFT
                    <input
                      name="ifsc_swift"
                      defaultValue={account?.ifsc_swift || ''}
                      className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                    />
                  </label>
                  <label className="text-sm font-semibold text-slate-800">
                    Country
                    <input
                      name="country"
                      defaultValue={account?.country || ''}
                      className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                    />
                  </label>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="text-sm font-semibold text-slate-800">
                    Mobile money provider
                    <input
                      name="mobile_money_provider"
                      defaultValue={account?.mobile_money_provider || ''}
                      className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                    />
                  </label>
                  <label className="text-sm font-semibold text-slate-800">
                    Mobile money number
                    <input
                      name="mobile_money_number"
                      defaultValue={account?.mobile_money_number || ''}
                      className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                    />
                  </label>
                </div>
                <div className="flex items-end">
                  <button
                    type="submit"
                    disabled={savingAccount}
                    className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-60"
                  >
                    {savingAccount ? 'Saving...' : 'Save payout account'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </HostSection>
    </div>
  );
};

export default HostEarnings;

